import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import {
  MapPin,
  Plus,
  Building2,
  Copy,
  AlertCircle,
} from 'lucide-react';
import { Site, createDefaultSite, validateSite } from '../types/siteTypes';
import SiteCard from './SiteCard';
import { deleteTenderSite } from '../services/tenderApiService';
import { useToast } from '../../../foundation/altzero_base/base/hooks/use-toast';
import type { NormalizedSalesforceImport } from '../types/salesforceTypes';

interface SitesManagementSectionProps {
  sites: Site[];
  onSitesChange: (sites: Site[]) => void;
  showErrors?: boolean;
  /**
   * The tender these sites belong to, once it exists. Removing a site has to delete its
   * tender_site row, not just drop it from this list — see handleRemoveSite. Absent before the
   * tender is first saved, which is exactly when there is no row to delete.
   */
  tenderId?: string | null;
  /** Forwarded from each SiteCard's Salesforce import — see SiteCard's own note. */
  onSalesforceConsultant?: (consultant: NormalizedSalesforceImport['consultant']) => void;
}

const SitesManagementSection: React.FC<SitesManagementSectionProps> = ({
  sites,
  onSitesChange,
  showErrors = false,
  tenderId,
  onSalesforceConsultant,
}) => {
  const { toast } = useToast();
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());

  // Auto-expand the first site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && expandedSites.size === 0) {
      setExpandedSites(new Set([sites[0].id]));
    }
  }, [sites, expandedSites.size]);

  // Auto-expand invalid sites when showErrors becomes true
  useEffect(() => {
    if (showErrors) {
      const invalidIds = sites
        .filter(site => !validateSite(site).isValid)
        .map(site => site.id);
      if (invalidIds.length > 0) {
        setExpandedSites(prev => new Set([...prev, ...invalidIds]));
      }
    }
  }, [showErrors]);

  // Add a new site
  const addSite = () => {
    const newSite = createDefaultSite();
    const updatedSites = [...sites, newSite];
    onSitesChange(updatedSites);

    // Auto-expand the new site
    setExpandedSites(prev => new Set([...prev, newSite.id]));
  };

  // Remove a site
  const handleRemoveSite = (siteId: string) => {
    const removed = sites.find(site => site.id === siteId);
    const updatedSites = sites.filter(site => site.id !== siteId);
    onSitesChange(updatedSites);

    // Remove from expanded sites
    setExpandedSites(prev => {
      const newSet = new Set(prev);
      newSet.delete(siteId);
      return newSet;
    });

    // Delete the tender_site row now. saveTenderSites only inserts and reuses by NMI — it has
    // never removed a row that the form no longer carries, and nothing else did either, so
    // without this the site stayed in the database and came back on the next load, while every
    // step that reads tender_site instead of this list went on pricing it. Same reason
    // OffersStep's handleRemoveOffer calls deleteOffer. No-op when the tender has not been
    // saved yet, or when this site was added here and never persisted.
    if (!tenderId || !removed?.nmi) return;
    deleteTenderSite(tenderId, removed.nmi).then(result => {
      if (!result.success) {
        console.error('❌ Failed to delete site from database:', result.error);
        toast({
          title: 'Delete failed',
          description: `Site removed here, but couldn't delete it from the database: ${result.error}`,
          variant: 'destructive',
        });
      }
    });
  };

  // Update a site - memoized to prevent input focus loss
  const handleUpdateSite = useCallback((updatedSite: Site) => {
    onSitesChange((prevSites) => 
      prevSites.map(site => 
        site.id === updatedSite.id ? updatedSite : site
      )
    );
  }, [onSitesChange]);

  // Copy a site
  const handleCopySite = (siteId: string) => {
    const siteToClone = sites.find(site => site.id === siteId);
    if (!siteToClone) return;

    const clonedSite: Site = {
      ...siteToClone,
      id: `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `${siteToClone.name} (Copy)`,
      nmi: '', // Clear NMI as it should be unique
      isNew: true,
      isExpanded: true,
    };

    const updatedSites = [...sites, clonedSite];
    onSitesChange(updatedSites);
    
    // Auto-expand the cloned site
    setExpandedSites(prev => new Set([...prev, clonedSite.id]));
  };

  // Toggle site expansion
  const handleToggleExpansion = (siteId: string) => {
    setExpandedSites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  // Calculate validation stats
  const validSites = sites.filter(site => validateSite(site).isValid);
  const totalSites = sites.length;

  return (
    <div className="space-y-6">
      {/* Sites Header */}
      <Card className="border border-primary/10 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold text-gray-900">
                  Site Information
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Add and manage energy sites for this client
                </p>
              </div>
            </div>
            
            {/* Site Stats */}
            <div className="flex items-center gap-4">
              {totalSites > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {validSites.length}/{totalSites} Complete
                  </Badge>
                  {validSites.length < totalSites && (
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                  )}
                </div>
              )}

              <Button
                onClick={addSite}
                className="bg-primary hover:bg-primary/90 text-white"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Site
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Sites List */}
      {sites.length === 0 ? (
        // Empty State
        <Card className="border-2 border-dashed border-gray-200 hover:border-primary/30 transition-colors">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No sites added yet
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Add your first energy site to get started. You can add multiple sites
                for clients with various locations.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button
                  onClick={addSite}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Site
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Sites Cards
        <div className="space-y-4">
          {sites.map((site, index) => (
            <SiteCard
              key={site.id}
              site={site}
              index={index}
              isExpanded={expandedSites.has(site.id)}
              onUpdate={handleUpdateSite}
              onSalesforceConsultant={onSalesforceConsultant}
              onRemove={handleRemoveSite}
              onCopy={handleCopySite}
              onToggleExpansion={handleToggleExpansion}
              showErrors={showErrors}
            />
          ))}
          
          {/* Add Another Site Button */}
          <div className="pt-4">
            <Button
              onClick={addSite}
              variant="outline"
              className="w-full border-2 border-dashed border-primary/30 hover:border-primary/50 hover:bg-primary/5 text-primary"
              size="lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Another Site
            </Button>
          </div>
        </div>
      )}

      {/* Sites Summary */}
      {sites.length > 0 && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <span className="text-gray-600">
                  <strong>{totalSites}</strong> site{totalSites !== 1 ? 's' : ''} added
                </span>
                <span className="text-gray-600">
                  <strong>{validSites.length}</strong> complete
                </span>
                {validSites.length < totalSites && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {totalSites - validSites.length} incomplete
                  </span>
                )}
              </div>

              <div className="text-xs text-gray-500">
                All sites must be complete to continue
              </div>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default SitesManagementSection;
